import { datadogLogs } from '@datadog/browser-logs'
import { useStoreActions } from 'easy-peasy'
import React, {
  createContext,
  type FC,
  useEffect,
  useMemo,
  useState
} from 'react'
import { useHistory } from 'react-router-dom'

interface WidgetContextType {
  settings: {
    isDraggable?: boolean
    isAlwaysOpen?: boolean
    copyNoteButtonText?: string
    patientReference?: string
    hideTreatmentPlan?: string
    hidePreviousSessions?: string
    isMinifiedView?: boolean
    fontHref?: string
    fontFamily?: string
    minifiedViewSettings?: {
      container?: {
        borderColor: string
        borderWidth: string
        borderRadius: string
        backgroundColor: string
      }
      buttons?: {
        borderRadius: string
        primaryBackgroundColor: string
        primaryTextColor: string
        primaryBorderColor: string
        primaryBorderWidth: string
        secondaryBackgroundColor: string
        secondaryTextColor: string
        secondaryBorderColor: string
        secondaryBorderWidth: string
      }
      inputs?: {
        borderRadius: string
      }
      audioIndicatorColor?: string
    }
  }
  selectedClientId?: string | undefined
  selectClientOptions?: {
    sessionExternalId?: string | undefined
    sessionSetting?: 'in-person' | 'telehealth' | undefined
    usingHeadphones?: boolean | undefined
    noteOptions?: {
      sessionType?: 'individual' | 'group' | 'couple' | undefined
      noteType?: string | undefined
      availableNoteTypes?: string[] | undefined
      treatmentApproaches?: string[] | undefined
      noteGroup?: 'therapists' | 'prescribers' | undefined
    }
  }
}

export const WidgetContext = createContext<WidgetContextType>(
  {} as WidgetContextType
)

interface WidgetProviderProps {
  children: React.ReactNode
}

export const WidgetProvider: FC<WidgetProviderProps> = ({ children }) => {
  const history = useHistory()
  const [settings, setSettings] = useState<WidgetContextType['settings']>({})
  const [selectedClientId, setSelectedClientId] = useState()
  const [selectClientOptions, setSelectClientOptions] = useState()
  const widgetLogin = useStoreActions(
    (actions: any) => actions.auth.widgetLogin
  )
  const logout = useStoreActions((actions: any) => actions.auth.logout)
  const isMiniWidget = window.location.pathname.includes('mini-widget')
  const contextValue = useMemo(
    () => ({ settings, selectedClientId, selectClientOptions }),
    [settings, selectedClientId, selectClientOptions]
  )

  const forceAuthenticate = async (authTokens: {
    AccessToken: string
    RefreshToken: string
  }) => {
    await widgetLogin(authTokens)
  }

  // support for custom fonts
  useEffect(() => {
    let link: HTMLLinkElement | undefined, style: HTMLStyleElement | undefined
    if (settings.fontHref && settings.fontFamily) {
      link = document.createElement('link')
      link.href = settings.fontHref
      link.rel = 'stylesheet'
      document.head.appendChild(link)

      style = document.createElement('style')
      style.innerHTML = `
        body {
          font-family: ${settings.fontFamily} !important;
        }
      `
      document.head.appendChild(style)
    }

    return () => {
      if (link) document.head.removeChild(link)
      if (style) document.head.removeChild(style)
    }
  }, [settings.fontHref])

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === 'BP_SETTINGS') {
        setSettings(event.data.settings)
      }

      if (event.data?.type === 'BP_AUTHENTICATE') {
        datadogLogs.logger.info(
          '[Auth Debug - Widget Context] Force Authenticate',
          {
            ...event.data
          }
        )

        forceAuthenticate(event.data.authTokens)
      }

      if (event.data?.type === 'BP_WIDGET_LOGOUT') {
        datadogLogs.logger.info('[Auth Debug - Widget Context] Widget Logout', {
          ...event.data
        })

        logout()
      }

      if (event.data?.type === 'BP_SELECT_CLIENT') {
        const clientId = event.data.clientId
        const options = event.data?.options

        if (options) {
          setSelectClientOptions(options)
        }
        setSelectedClientId(clientId)
      }
    }

    window.addEventListener('message', handleMessage)

    // eslint-disable-next-line
    parent.postMessage({ type: 'BP_EXTENSION_CONTEXT_LOADED' }, '*')
    // eslint-disable-next-line
    parent.postMessage({ type: 'BP_WIDGET_CONTEXT_LOADED' }, '*')

    return () => window.removeEventListener('message', handleMessage)
  }, [])

  useEffect(() => {
    if (selectedClientId)
      history.push(`${isMiniWidget ? '/mini-widget' : '/widget'}/start-session`)
  }, [selectedClientId])

  return (
    <WidgetContext.Provider value={contextValue}>
      {children}
    </WidgetContext.Provider>
  )
}
